import React, { useEffect, useState } from "react";
import { FileText, Download, Search, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, LoadingBlock, EmptyState } from "../../components/app/ui";
import { payoutApi, errorMessage } from "../../lib/api";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function GstInvoices() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [resending, setResending] = useState(false);

  const load = (query) => payoutApi.adminGstInvoices(query).then(setData).catch(() => setData({ invoices: [], count: 0 }));
  useEffect(() => { load(); }, []);

  const search = (e) => { e.preventDefault(); load(q.trim() || undefined); };

  const resend = async () => {
    if (!window.confirm("Re-send this week's GST invoice emails to restaurant owners for the most recent payout run? No new settlement is created.")) return;
    setResending(true);
    try {
      const r = await payoutApi.adminResendInvoices();
      toast.success(`Re-sent ${r.sent} of ${r.restaurant_invoices} invoice email${r.restaurant_invoices === 1 ? "" : "s"} for the latest run.`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setResending(false); }
  };

  if (data === null) return <><PageHeader title="GST invoices" /><LoadingBlock rows={4} /></>;
  const rows = data.invoices || [];

  return (
    <>
      <PageHeader title="GST invoices" description="Register of every issued restaurant GST tax invoice — number, owner, period and totals." />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={search} className="flex items-center gap-2" data-testid="gst-invoice-search-form">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice no, restaurant or owner" className="w-72" data-testid="gst-invoice-search" />
          <Button type="submit" variant="outline" className="rounded-full" data-testid="gst-invoice-search-btn"><Search className="h-4 w-4" /></Button>
        </form>
        <Button onClick={resend} disabled={resending} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="gst-invoice-resend-btn">
          {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Re-send this week's invoices
        </Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={FileText} title="No GST invoices yet" text="Restaurant GST invoices are issued automatically on each weekly payout run." />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-surface-200" data-testid="gst-invoice-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Invoice no</th>
                <th className="px-4 py-3">Restaurant / owner</th>
                <th className="px-4 py-3">Pay period</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Commission</th>
                <th className="px-4 py-3 text-right">GST (18%)</th>
                <th className="px-4 py-3 text-right">Net paid</th>
                <th className="px-4 py-3 text-right">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.payout_id} className="border-t border-surface-200" data-testid={`gst-invoice-row-${r.payout_id}`}>
                  <td className="px-4 py-3 font-semibold text-ink" data-testid={`gst-invoice-no-${r.payout_id}`}>{r.invoice_no}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink">{r.restaurant_name}</div>
                    <div className="text-xs text-ink-muted">{r.owner_name || "—"}{r.owner_email ? ` · ${r.owner_email}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{r.period}</td>
                  <td className="px-4 py-3 text-right text-ink-soft">{money(r.gross)}</td>
                  <td className="px-4 py-3 text-right text-ink-soft">{money(r.commission)}</td>
                  <td className="px-4 py-3 text-right text-ink-soft">{money(r.commission_gst)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-ink">{money(r.net)}</td>
                  <td className="px-4 py-3 text-right">
                    <a href={payoutApi.gstInvoiceUrl(r.payout_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline" data-testid={`gst-invoice-download-${r.payout_id}`}><Download className="h-3.5 w-3.5" /> PDF</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-ink-muted">{rows.length} invoice{rows.length === 1 ? "" : "s"} shown.</p>
    </>
  );
}
